/**
 * Producer-side broker for `window.vizLoader`.
 *
 * The flooring visualizer builds a brand-new loader instance every time
 * WebVisualizer mounts, while analytics subscribers — the vendored mosaic
 * feature here, or an external per-retailer bundle on other surfaces — call
 * `window.vizLoader.addListener(...)` exactly once, on the first page they
 * load. Under client-side navigation that first page often has no visualizer
 * at all, and even after a subscriber binds, the next PDP mounts a *different*
 * instance and orphans the listener. Either way, later visualizations fire no
 * events.
 *
 * The broker replaces the raw loader with a stable facade that outlives any
 * single instance. It records the subscriptions registered through it and
 * replays them onto whichever real loader is current, forwarding sendEvent /
 * removeListener (and any other property access) to that loader. Subscribers
 * subscribe once and stay wired up across every navigation. Because it sits on
 * the producer side it is subscriber-agnostic: the same fix works regardless of
 * which analytics bundle is listening.
 */

type Handler = (...args: unknown[]) => void;
type Listener = { event: string; handler: Handler };

interface RealLoader {
  addListener: (event: string, handler: Handler) => void;
  removeListener?: (event: string, handler: Handler) => void;
  sendEvent: (...args: unknown[]) => unknown;
  [key: string]: unknown;
}

// Marks a loader instance with the set of broker-owned listeners already bound
// to it, so repeated __setInstance calls never double-subscribe the same one.
const BOUND = '__vizBrokerBound';

interface BrokerCore {
  __isVizBroker: true;
  __instance: RealLoader | null;
  __listeners: Listener[];
  __setInstance: (loader: RealLoader) => void;
  addListener: (event: string, handler: Handler) => void;
  removeListener: (event: string, handler: Handler) => void;
  sendEvent: (...args: unknown[]) => unknown;
}

// Property names handled by the broker itself; everything else the facade
// forwards straight through to the current real loader instance.
const OWN_KEYS = new Set<string | symbol>([
  '__isVizBroker',
  '__instance',
  '__listeners',
  '__setInstance',
  'addListener',
  'removeListener',
  'sendEvent',
]);

function isBroker(value: unknown): boolean {
  return (
    value != null &&
    (value as { __isVizBroker?: unknown }).__isVizBroker === true
  );
}

function bindListener(loader: RealLoader, listener: Listener): void {
  const bound = (loader[BOUND] as Listener[] | undefined) ?? [];
  if (bound.includes(listener)) return;
  bound.push(listener);
  loader[BOUND] = bound;
  loader.addListener(listener.event, listener.handler);
}

function unbindListener(loader: RealLoader, listener: Listener): void {
  loader.removeListener?.(listener.event, listener.handler);
  const bound = loader[BOUND] as Listener[] | undefined;
  if (bound) {
    const idx = bound.indexOf(listener);
    if (idx !== -1) bound.splice(idx, 1);
  }
}

/**
 * Ensure `window.vizLoader` is the stable broker facade and return it.
 * Idempotent and safe to call on every page load and every mount.
 */
export function installVizLoaderBroker(): unknown {
  if (typeof window === 'undefined') return null;

  const existing = window.vizLoader;
  if (isBroker(existing)) return existing;

  const listeners: Listener[] = [];

  const core: BrokerCore = {
    __isVizBroker: true,
    __instance: null,
    __listeners: listeners,
    __setInstance(loader: RealLoader) {
      const previous = core.__instance;
      if (previous === loader) return;
      // Detach our replayed listeners from the instance we are replacing so a
      // lingering old loader can't keep firing duplicate events.
      if (previous) {
        for (const listener of listeners) unbindListener(previous, listener);
      }
      core.__instance = loader;
      for (const listener of listeners) bindListener(loader, listener);
    },
    addListener(event: string, handler: Handler) {
      // Ignore an identical (event, handler) already recorded, so a subscriber
      // whose init runs more than once can't double-dispatch the same event.
      if (listeners.some((l) => l.event === event && l.handler === handler)) {
        return;
      }
      const listener: Listener = { event, handler };
      listeners.push(listener);
      if (core.__instance) bindListener(core.__instance, listener);
    },
    removeListener(event: string, handler: Handler) {
      const idx = listeners.findIndex(
        (l) => l.event === event && l.handler === handler
      );
      const [listener] = idx !== -1 ? listeners.splice(idx, 1) : [];
      if (core.__instance && listener) {
        unbindListener(core.__instance, listener);
      }
    },
    sendEvent(...args: unknown[]) {
      return core.__instance?.sendEvent(...args);
    },
  };

  const facade = new Proxy(core, {
    get(target, prop, receiver) {
      if (OWN_KEYS.has(prop)) return Reflect.get(target, prop, receiver);
      const instance = target.__instance;
      if (!instance) return undefined;
      const value = instance[prop as string];
      return typeof value === 'function' ? value.bind(instance) : value;
    },
    set(target, prop, value, receiver) {
      if (OWN_KEYS.has(prop)) return Reflect.set(target, prop, value, receiver);
      const instance = target.__instance;
      if (instance) instance[prop as string] = value;
      return true;
    },
    has(target, prop) {
      if (OWN_KEYS.has(prop)) return true;
      const instance = target.__instance;
      return instance ? prop in instance : false;
    },
  });

  // Adopt a real loader that was assigned before the broker installed, so no
  // instance or subscription is lost in a race.
  if (existing != null && typeof existing.addListener === 'function') {
    core.__setInstance(existing as RealLoader);
  }

  window.vizLoader = facade;
  return facade;
}

/**
 * Register a freshly-constructed loader as the current instance behind the
 * broker facade, installing the broker first if it isn't present yet.
 */
export function setVizLoaderInstance(loader: RealLoader): void {
  if (typeof window === 'undefined') return;
  installVizLoaderBroker();
  const current = window.vizLoader;
  if (isBroker(current)) {
    (current as unknown as BrokerCore).__setInstance(loader);
  } else {
    // Broker unavailable (SSR / unexpected) — fall back to the raw assignment
    // so behavior is no worse than before.
    window.vizLoader = loader;
  }
}

/**
 * Whether a real loader instance is currently attached and drivable.
 *
 * The broker facade is installed from page load, so `window.vizLoader` is truthy
 * even before any visualizer exists. Call sites that gate behavior on the
 * visualizer actually being ready (e.g. forwarding sendEvent) must use this
 * instead of a bare `window.vizLoader` truthiness check.
 */
export function hasVizLoaderInstance(): boolean {
  if (typeof window === 'undefined') return false;
  const current = window.vizLoader;
  if (current == null) return false;
  if (isBroker(current)) {
    return (current as unknown as BrokerCore).__instance != null;
  }
  // A raw loader (SSR fallback path) counts as ready.
  return true;
}
